import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { crud } from '@/common/crud';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { BN_AuthRegister, BN_AuthRegisterLedgerItem } from '@/fhirworks';

const { postTransaction } = useWorkflow();

async function getChargeItemAuths(patients, services, procedures, chargeItem, isNew = false, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (services?.length) {
        for (const patient of patients) {
            if (!patient?.id) continue;
            let claimResponseResult = null;
            let coverageResult = null;

            try {
                coverageResult = await $httpFhirApi.get(`/Coverage?beneficiary=${patient.id}&status=active`);
            } catch (error) {
                console.error('Error fetching Coverage for patient', patient.id, error);
            }

            try {
                claimResponseResult = await $httpFhirApi.get(`/ClaimResponse?patient=${patient.id}&use=preauthorization&status=active`);
            } catch (error) {
                console.error('Error fetching ClaimResponse for patient', patient.id, error);
            }

            if (claimResponseResult?.data?.length > 0 && coverageResult?.data?.length > 0) {
                const activeCoveragePayorUris = new Set(coverageResult.data.map((coverage) => coverage.payor?.[0]?.uri).filter(Boolean));
                const sortedClaimResponse = claimResponseResult.data
                    .filter((claimResponse) => activeCoveragePayorUris.has(claimResponse?.insurer?.uri))
                    .sort((a, b) => {
                        const aStart = a.preAuthPeriod?.start || a.created;
                        const bStart = b.preAuthPeriod?.start || b.created;
                        return new Date(aStart) - new Date(bStart);
                    });

                if (sortedClaimResponse.length === 0) {
                    continue;
                }

                const existingRegisters = await $httpFhirApi.get(`/BN_AuthRegister?patient=${patient.id}&auth=${sortedClaimResponse.map((claimResponse) => claimResponse.id).join(',')}`);
                const authTransaction = await calculateAuths(sortedClaimResponse, patient, existingRegisters.data, chargeItem, isNew);
                transaction.push(...authTransaction);
            }
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function calculateAuths(sortedClaimResponse, patient, existingRegisters, chargeItem, isNew = false) {
    let transaction = [];

    for (const claimResponse of sortedClaimResponse) {
        // Find existing register for this patient + claimResponse combination
        let existingRegister = existingRegisters.find((e) => e.auth.id === claimResponse.id && e.patient.id === patient.id);
        let newLedgerEntries = [];

        // If no existing register, initialize with auth totals from ALL items in this ClaimResponse
        if (!existingRegister?.ledger?.length) {
            for (const item of claimResponse.item) {
                const authUnits = item.authorizedItemDetail?.quantity?.value;
                const authAmount = item.authorizedItemDetail?.unitPrice?.value;

                if (authAmount !== undefined) {
                    newLedgerEntries.push(
                        new BN_AuthRegisterLedgerItem({
                            date: new Date().toISOString(),
                            amount: { value: authAmount },
                            unit: null,
                        }),
                    );
                }
                if (authUnits !== undefined) {
                    newLedgerEntries.push(
                        new BN_AuthRegisterLedgerItem({
                            date: new Date().toISOString(),
                            amount: null,
                            unit: authUnits,
                        }),
                    );
                }
            }
        }

        // Process items that match our current charge item
        for (const item of claimResponse.item) {
            if (item.authorizedItemDetail?.productOrService?.coding?.[0]?.display !== chargeItem?.code?.coding?.[0]?.display) continue;

            // Link ClaimResponse to ChargeItem via supportingInformation (not direct authNumber)
            if (!isNew && item.preAuthNumber) {
                // For existing ChargeItems, ensure ClaimResponse is in supportingInformation
                const claimResponseRef = {
                    id: claimResponse.id,
                    resourceType: 'ClaimResponse',
                };

                // Check if ClaimResponse is already in supportingInformation
                const hasClaimResponse = chargeItem.supportingInformation?.some((ref) => ref.resourceType === 'ClaimResponse' && ref.id === claimResponse.id);

                if (!hasClaimResponse) {
                    // Clone the chargeItem and update supportingInformation
                    const updatedChargeItem = { ...chargeItem };
                    updatedChargeItem.supportingInformation = [...(chargeItem.supportingInformation || []), claimResponseRef];

                    // Use crud.patch for efficient PATCH operation
                    const patchPayload = crud.patch(updatedChargeItem, chargeItem);
                    if (patchPayload) {
                        transaction.push(patchPayload);
                    }
                }
            }

            if (isNew && item.preAuthNumber) {
                // For new ChargeItems, add ClaimResponse to supportingInformation
                const claimResponseRef = {
                    id: claimResponse.id,
                    resourceType: 'ClaimResponse',
                };

                if (!chargeItem.supportingInformation) {
                    chargeItem.supportingInformation = [];
                }

                // Check if ClaimResponse is already in supportingInformation
                const hasClaimResponse = chargeItem.supportingInformation.some((ref) => ref.resourceType === 'ClaimResponse' && ref.id === claimResponse.id);

                if (!hasClaimResponse) {
                    chargeItem.supportingInformation.push(claimResponseRef);
                }
            }

            const unitsUsed = chargeItem?.quantity?.value || 1;
            const baseCharge = chargeItem?.priceOverride?.value || 0;
            const deduction = Math.round(baseCharge * unitsUsed);

            const authUnits = item.authorizedItemDetail?.quantity?.value;
            const authAmount = item.authorizedItemDetail?.unitPrice?.value;

            // Add deduction entries for this specific charge item
            if (authUnits !== undefined) {
                newLedgerEntries.push(
                    new BN_AuthRegisterLedgerItem({
                        date: new Date().toISOString(),
                        amount: null,
                        unit: -unitsUsed,
                        chargeItem: { id: chargeItem.id, resourceType: 'ChargeItem' },
                    }),
                );
            }

            if (authAmount !== undefined) {
                newLedgerEntries.push(
                    new BN_AuthRegisterLedgerItem({
                        date: new Date().toISOString(),
                        amount: { value: -deduction },
                        unit: null,
                        chargeItem: { id: chargeItem.id, resourceType: 'ChargeItem' },
                    }),
                );
            }
        }

        let authRegisterEntry;
        if (existingRegister?.id) {
            existingRegister.ledger.push(...newLedgerEntries);

            let totalsObj = {
                baseUnit: undefined,
                baseAmount: undefined,
                currentUnit: 0,
                currentAmount: 0,
            };

            existingRegister.ledger.forEach((ledgerItem) => {
                if (ledgerItem.chargeItem) {
                    if (ledgerItem.unit) {
                        totalsObj.currentUnit = (totalsObj.currentUnit || 0) + ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.currentAmount = (totalsObj.currentAmount || 0) + ledgerItem.amount?.value;
                    }
                } else {
                    if (ledgerItem.unit) {
                        totalsObj.baseUnit = ledgerItem.unit;
                        totalsObj.currentUnit = ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.baseAmount = ledgerItem.amount?.value;
                        totalsObj.currentAmount = ledgerItem.amount?.value;
                    }
                }
            });

            // If either currentAmount or currentUnit is <= 0 add exhausted property
            if (totalsObj.currentAmount <= 0 || totalsObj.currentUnit <= 0) {
                existingRegister.exhausted = true;
            }

            authRegisterEntry = crud.patch(existingRegister);
        } else {
            const authRegisterToSave = new BN_AuthRegister({
                patient: { id: patient.id, resourceType: 'Patient' },
                auth: { id: claimResponse.id, resourceType: 'ClaimResponse' },
                ledger: newLedgerEntries,
            });

            // Calculate totals for new register
            let totalsObj = {
                baseUnit: undefined,
                baseAmount: undefined,
                currentUnit: 0,
                currentAmount: 0,
            };

            newLedgerEntries.forEach((ledgerItem) => {
                if (ledgerItem.chargeItem) {
                    if (ledgerItem.unit) {
                        totalsObj.currentUnit = (totalsObj.currentUnit || 0) + ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.currentAmount = (totalsObj.currentAmount || 0) + ledgerItem.amount?.value;
                    }
                } else {
                    if (ledgerItem.unit) {
                        totalsObj.baseUnit = ledgerItem.unit;
                        totalsObj.currentUnit = ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.baseAmount = ledgerItem.amount?.value;
                        totalsObj.currentAmount = ledgerItem.amount?.value;
                    }
                }
            });

            if (totalsObj.currentAmount <= 0 || totalsObj.currentUnit <= 0) {
                authRegisterToSave.exhausted = true;
            }

            authRegisterEntry = crud.post(authRegisterToSave);
        }
        transaction.push(authRegisterEntry);
    }

    return transaction;
}
const extractAuthRegisters = (response) => {
    let resources = [];
    if (response.data && response.data.entry && response.data.entry.length > 0) {
        resources = response.data.entry.map((entry) => entry.resource);
    } else if (response.data && Array.isArray(response.data)) {
        resources = response.data;
    } else if (response.data && response.data.resourceType) {
        resources = [response.data];
    }
    return resources;
};

async function removeAuthTrackingForChargeItems(chargeItemIds, patientId = null, existingTransaction = false) {
    let transaction = existingTransaction || [];
    const searchPatientId = patientId;

    if (!searchPatientId) {
        return transaction;
    }

    // Search by patient(s)
    const response = await $httpFhirApi.get(`BN_AuthRegister?patient=${searchPatientId}`);

    const authRegisters = extractAuthRegisters(response);

    // Filter to only include auth registers that have ledger entries referencing our charge items
    const chargeItemIdsSet = new Set(Array.isArray(chargeItemIds) ? chargeItemIds : [chargeItemIds]);

    const filteredAuthRegisters = authRegisters.filter((authRegister) => {
        if (!authRegister.ledger || !Array.isArray(authRegister.ledger)) {
            return false;
        }

        // Check if any ledger entry references one of our charge items
        const hasMatchingChargeItem = authRegister.ledger.some((ledgerItem) => {
            const chargeItemRef = ledgerItem.chargeItem;
            if (!chargeItemRef) {
                return false;
            }

            const chargeItemId = chargeItemRef.id || chargeItemRef.reference?.split('/').pop();
            const matches = chargeItemIdsSet.has(chargeItemId);
            return matches;
        });

        return hasMatchingChargeItem;
    });

    // For each auth register, remove ledger entries for the deleted charge items
    for (const authRegister of filteredAuthRegisters) {
        const updatedLedger = authRegister.ledger.filter((ledgerItem) => {
            const chargeItemRef = ledgerItem.chargeItem;
            if (!chargeItemRef) {
                return true;
            }

            const chargeItemId = chargeItemRef.id || chargeItemRef.reference?.split('/').pop();
            return !chargeItemIdsSet.has(chargeItemId);
        });

        // If no ledger entries remain, delete the entire auth register
        if (updatedLedger.length === 0) {
            transaction.push(crud.delete(authRegister));
        } else {
            authRegister.ledger = updatedLedger;

            let totalsObj = {
                baseUnit: undefined,
                baseAmount: undefined,
                currentUnit: 0,
                currentAmount: 0,
            };

            updatedLedger.forEach((ledgerItem) => {
                if (ledgerItem.chargeItem) {
                    if (ledgerItem.unit) {
                        totalsObj.currentUnit = (totalsObj.currentUnit || 0) + ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.currentAmount = (totalsObj.currentAmount || 0) + ledgerItem.amount?.value;
                    }
                } else {
                    if (ledgerItem.unit) {
                        totalsObj.baseUnit = ledgerItem.unit;
                        totalsObj.currentUnit = ledgerItem.unit;
                    }
                    if (ledgerItem.amount?.value) {
                        totalsObj.baseAmount = ledgerItem.amount?.value;
                        totalsObj.currentAmount = ledgerItem.amount?.value;
                    }
                }
            });

            if (totalsObj.currentAmount <= 0 || totalsObj.currentUnit <= 0) {
                authRegister.exhausted = true;
            } else {
                authRegister.exhausted = false;
            }

            transaction.push(crud.patch(authRegister));
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

export function useAuthFlow() {
    return {
        getChargeItemAuths,
        calculateAuths,
        removeAuthTrackingForChargeItems,
    };
}
